import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { api, resolveMediaUrl } from "@/lib/api";
import { drawPlate, loadImage, loadSilhouetteImage, loadPlateFont } from "@/lib/platePainter";

/**
 * Large CTA banner on the home page pointing to /generator-tablic.
 *
 * The mini plate on the right side is rendered via the *same* canvas
 * painter used on the /generator-tablic page (`drawPlate` from
 * `@/lib/platePainter`). This guarantees pixel-perfect proportions and
 * legible text at any breakpoint, unlike the previous CSS/HTML mockup
 * which was cropping the strip/text at narrower widths.
 */
export default function PlateGeneratorBanner() {
    const canvasRef = useRef(null);
    const [logoImg, setLogoImg] = useState(null);
    const [hologramImg, setHologramImg] = useState(null);
    const [eagleImg, setEagleImg] = useState(null);
    const [plateFontUrl, setPlateFontUrl] = useState("");

    // Load the site plate-logo (falls back to the general site logo)
    // so the banner's mini plate always mirrors what the real generator
    // renders. Failure to load is silent - the fallback 12-star EU emblem
    // will just be drawn instead. We also capture the admin-uploaded
    // plate font URL, if any, so the banner uses the exact same glyphs
    // as /generator-tablic.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get("/mod-info");
                const url =
                    r.data?.plate_logo_url || r.data?.logo_url || "";
                if (!cancelled) setPlateFontUrl(r.data?.plate_font_url || "");
                if (!url) return;
                const img = await loadImage(resolveMediaUrl(url));
                if (!cancelled) setLogoImg(img);
            } catch (e) {
                /* non-fatal */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // Preload the legalization-sticker assets (hologram + eagle) so the
    // mini plate on the home banner matches the /generator-tablic page.
    // The eagle is preprocessed into an alpha silhouette so it reads
    // clearly against the shiny hologram background.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const [holo, eagle] = await Promise.all([
                loadImage("/plate-assets/holo.png"),
                loadSilhouetteImage("/plate-assets/eagle.png"),
            ]);
            if (cancelled) return;
            setHologramImg(holo);
            setEagleImg(eagle);
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // Paint the mini plate. We use scale 0.25 (BASE_W * 0.25 = 481 px on
    // the canvas) - plenty of resolution for a 360-420 px display width
    // even on hi-dpi screens, without wasting memory. We repaint once
    // the plate fonts are actually loaded so the first render isn't a
    // "wrong font" flash. If the admin has uploaded a custom plate font
    // we load and register it via FontFace too.
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const paint = () => {
            drawPlate(canvas, {
                prefix: "SL",
                tail: "12345",
                logoImg,
                hologramImg,
                eagleImg,
                scale: 0.25,
            });
        };
        paint();
        const adminFontPromise = plateFontUrl
            ? loadPlateFont(resolveMediaUrl(plateFontUrl))
            : Promise.resolve(false);
        if (typeof document !== "undefined" && document.fonts) {
            Promise.all([
                document.fonts.load('900 200px "Arklatrs"'),
                document.fonts.load('900 200px "License Plate"'),
                document.fonts.load('900 200px "Anton"'),
                adminFontPromise,
            ])
                .then(paint)
                .catch(() => {});
        }
    }, [logoImg, hologramImg, eagleImg, plateFontUrl]);

    return (
        <section
            id="generator-tablic-banner"
            data-testid="plate-generator-banner"
            className="section-padding-y"
        >
            <div className="max-w-[1400px] mx-auto px-6">
                <Link
                    to="/generator-tablic"
                    aria-label="Otwórz generator polskich tablic rejestracyjnych"
                    className="group relative overflow-hidden block rounded-3xl border border-[color:var(--primary)]/40 bg-gradient-to-br from-[color:var(--primary)]/25 via-[color:var(--bg-base)] to-black/60 hover:border-[color:var(--primary)]/70 transition-colors"
                >
                    {/* Decorative glow blobs */}
                    <div className="pointer-events-none absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-[color:var(--primary)]/25 blur-[100px]" />
                    <div className="pointer-events-none absolute -bottom-24 -right-24 w-[420px] h-[420px] rounded-full bg-[color:var(--primary)]/15 blur-[100px]" />

                    <div className="relative grid md:grid-cols-[1fr_1.05fr] gap-8 lg:gap-12 items-center p-6 sm:p-10 lg:p-14">
                        {/* Text side */}
                        <div>
                            <div className="inline-flex items-center gap-2 rounded-full border border-[color:var(--primary)]/40 bg-[color:var(--primary)]/10 px-3 py-1 text-[10px] uppercase tracking-[0.3em] text-[color:var(--primary)] mb-5">
                                <Sparkles className="w-3 h-3" />
                                Nowość
                            </div>
                            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-tight text-[color:var(--text-primary)]">
                                Wygeneruj własną
                                <span className="block bg-clip-text text-transparent bg-gradient-to-r from-[color:var(--primary)] to-yellow-300">
                                    tablicę z wyróżnikiem SL
                                </span>
                            </h2>
                            <p className="text-[color:var(--text-secondary)] max-w-lg mt-4 text-sm sm:text-base">
                                Polska tablica rejestracyjna 520×114 mm,
                                fotorealistyczna czcionka tablicowa, efekt
                                wytłoczenia i błyszcząca ramka. Pobierzesz
                                gotowy plik PNG w kilku rozdzielczościach -
                                od SD po 4K do druku.
                            </p>

                            <div className="mt-7 flex flex-wrap items-center gap-3">
                                <span className="inline-flex items-center gap-2 rounded-lg bg-[color:var(--primary)] text-[color:var(--bg-base)] px-5 py-3 text-xs uppercase tracking-[0.25em] font-black group-hover:bg-yellow-300 transition-colors">
                                    Otwórz generator
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </span>
                                <span className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-muted)]">
                                    PL • Polska • Śląskie
                                </span>
                            </div>
                        </div>

                        {/* Mini plate mockup (canvas) */}
                        <div className="relative flex justify-center md:justify-end">
                            <div className="plate-mini-scene">
                                <canvas
                                    ref={canvasRef}
                                    data-testid="plate-banner-canvas"
                                    className="plate-mini-canvas"
                                />
                            </div>
                        </div>
                    </div>
                </Link>
            </div>

            <style>{`
                .plate-mini-scene {
                    perspective: 1200px;
                    display: flex;
                    justify-content: center;
                    width: 100%;
                }
                .plate-mini-canvas {
                    width: min(460px, 92%);
                    height: auto;
                    display: block;
                    border-radius: 14px;
                    transform: rotateX(10deg) rotateY(-6deg);
                    transform-style: preserve-3d;
                    filter:
                        drop-shadow(0 24px 30px rgba(0,0,0,0.5))
                        drop-shadow(0 6px 10px rgba(0,0,0,0.35));
                    transition: transform 0.35s ease-out;
                }
                .group:hover .plate-mini-canvas {
                    transform: rotateX(6deg) rotateY(3deg);
                }
                @media (max-width: 640px) {
                    .plate-mini-canvas {
                        transform: rotateX(4deg) rotateY(-1deg);
                    }
                }
            `}</style>
        </section>
    );
}
